var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useStore } from "react-redux";
import { AUDIT_BALLOT } from "../queries/AuditBallot";
import { provideBallotService } from "../services/BallotService";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { EBallotAuditStatus, markBallotAudited, selectBallotAuditStatus, selectBallotForAudit, } from "../store/auditableBallots/auditableBallotsSlice";
import { clearReceivedBallot, selectReceivedBallot, } from "../store/receivedBallots/receivedBallotsSlice";
import { clearSessionStorageBallotData } from "../store/castVotes/sessionBallotData";
/** Reveal audit data only after the ballot box can no longer accept its cast. */
export var useAuditBallot = function (electionId, serverReceiptRequired) {
    var dispatch = useAppDispatch();
    var store = useStore();
    var ballot = useAppSelector(selectBallotForAudit(electionId));
    var status = useAppSelector(selectBallotAuditStatus(electionId));
    var received = useAppSelector(selectReceivedBallot(electionId));
    var auditBallot = useMutation(AUDIT_BALLOT)[0];
    var _a = useState(false), error = _a[0], setError = _a[1];
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    var attempted = useRef(undefined);
    var audit = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var result, answer, _a;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    if (!ballot || status === EBallotAuditStatus.AUDITED)
                        return [2 /*return*/];
                    setError(false);
                    setLoading(true);
                    _e.label = 1;
                case 1:
                    _e.trys.push([1, 4, 5, 6]);
                    if (!serverReceiptRequired) return [3 /*break*/, 3];
                    if (!received || received.ballot_hash !== ballot.ballot_hash)
                        throw new Error("The received ballot is unavailable");
                    return [4 /*yield*/, auditBallot({
                            variables: { electionId: electionId, ballotId: received.ballot_id },
                        })];
                case 2:
                    result = _e.sent();
                    answer = (_b = result.data) === null || _b === void 0 ? void 0 : _b.audit_ballot;
                    if (result.error ||
                        (answer === null || answer === void 0 ? void 0 : answer.ballot_id) !== received.ballot_id ||
                        answer.status !== "audited")
                        throw new Error("The ballot box did not confirm the audit");
                    _e.label = 3;
                case 3:
                    // The user may have gone Back and encrypted a new ballot while the
                    // request was pending. Never invalidate that new ballot or its key.
                    if (((_d = (_c = store.getState().auditableBallots[electionId]) === null || _c === void 0 ? void 0 : _c.auditableBallot) === null || _d === void 0 ? void 0 : _d.ballot_hash) ===
                        ballot.ballot_hash) {
                        provideBallotService().forgetVoterSigningKey(electionId);
                        dispatch(markBallotAudited({ electionId: electionId, ballotHash: ballot.ballot_hash }));
                        dispatch(clearReceivedBallot(electionId));
                        clearSessionStorageBallotData();
                    }
                    return [3 /*break*/, 6];
                case 4:
                    _a = _e.sent();
                    setError(true);
                    return [3 /*break*/, 6];
                case 5:
                    setLoading(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [auditBallot, ballot, dispatch, electionId, received, serverReceiptRequired, status, store]);
    useEffect(function () {
        if (!ballot || status === EBallotAuditStatus.AUDITED)
            return;
        var key = "".concat(electionId, ":").concat(ballot.ballot_hash);
        if (attempted.current === key)
            return;
        attempted.current = key;
        void audit();
    }, [audit, ballot, electionId, status]);
    return { ready: status === EBallotAuditStatus.AUDITED, loading: loading, error: error, retry: audit };
};
